import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Tag as AntTag,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import ReactECharts from 'echarts-for-react';
import { FieldDictParams, getPetFieldDictListApi, getPetFieldListApi } from '@/api/petManage/petFieldDict';
import {
  addPetTagApi,
  deletePetTagApi,
  getPetTagDataApi,
  getPetTagDetailApi,
  getPetTagPageListApi,
  updatePetTagApi,
  updatePetTagSortApi,
} from '@/api/petManage/tagManage';
import { PET_TAG_KEY } from '@/router/Permissions';
import { checkPermissions } from '@/utils/utils';

const petTypeOptions = [
  { label: '狗', value: '1' },
  { label: '猫', value: '2' },
];

const displayOptions = [
  { label: '显示', value: 1 },
  { label: '隐藏', value: 0 },
];

interface TagRecord {
  id: number;
  tagName: string;
  petType: string | number;
  isShow: number;
  sortNum: number;
  fieldNameList: string[];
  createTime?: string;
}

const TagManage: React.FC = () => {
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<TagRecord[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [fieldOptions, setFieldOptions] = useState<{ label: string; value: string }[]>([]);
  const [dictOptionsMap, setDictOptionsMap] = useState<Record<string, { label: string; value: string }[]>>({});
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [currentTag, setCurrentTag] = useState<any>(null);
  const [dataDrawerVisible, setDataDrawerVisible] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [chartData, setChartData] = useState<any>({ petTagList: [], dogTagList: [], catTagList: [] });
  const [dataTitle, setDataTitle] = useState<string>('');
  const [toggleId, setToggleId] = useState<number | null>(null);
  const [viewPetType, setViewPetType] = useState<string | number>('');
  const canAdd = useMemo(() => checkPermissions(PET_TAG_KEY.ADD), []);
  const canEdit = useMemo(() => checkPermissions(PET_TAG_KEY.EDIT), []);
  const canDelete = useMemo(() => checkPermissions(PET_TAG_KEY.DELETE), []);
  const canToggleShow = useMemo(() => checkPermissions(PET_TAG_KEY.SHOW), []);
  const canViewData = useMemo(() => checkPermissions(PET_TAG_KEY.DATA), []);
  const canSortUp = useMemo(() => checkPermissions(PET_TAG_KEY.UP), []);
  const canSortDown = useMemo(() => checkPermissions(PET_TAG_KEY.DOWN), []);

  const buildFieldDictParams = (fieldCode: string): FieldDictParams => {
    if (!fieldCode) return {};
    return fieldCode === 'bodySize' ? { fieldCode, petType: '1' } : { fieldCode };
  };

  const formatDictOptions = (fieldCode: string, list: any[] = []) => {
    const seen = new Set<string>();
    return list
      .map((item: any) => ({ label: item.dictName, value: String(item.dictCode) }))
      .filter(opt => {
        if (fieldCode === 'dailyActivityMagnitude') {
          if (!['1', '2', '3'].includes(opt.value)) return false;
          if (seen.has(opt.value)) return false;
          seen.add(opt.value);
          return true;
        }
        return true;
      });
  };

  const fetchList = async (override?: Partial<{ pageNum: number; pageSize: number }>) => {
    const values = form.getFieldsValue();
    const petType =
      values.petType === '' || values.petType === undefined || values.petType === null
        ? undefined
        : Number(values.petType);
    const params = {
      petType,
      tagName: values.tagName || undefined,
      isShow: values.isShow === '' ? undefined : values.isShow,
      pageNum: override?.pageNum || pageNum,
      pageSize: override?.pageSize || pageSize,
    };
    setLoading(true);
    try {
      const res = await getPetTagPageListApi(params);
      if (res.code === 200 || res.code === 0) {
        const rows = res.data?.rows || res.rows || [];
        const totalVal = res.data?.total ?? res.total ?? 0;
        setDataSource(rows);
        setTotal(totalVal);
        if (override?.pageNum) setPageNum(override.pageNum);
        if (override?.pageSize) setPageSize(override.pageSize);
      }
    } catch (error) {
      console.error('获取标签列表失败', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList({ pageNum: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchFields = async () => {
      try {
        const res = await getPetFieldListApi();
        if (res.code === 200) {
          const mergedMap = new Map<string, { label: string; value: string }>();
          (res.data || []).forEach((item: any) => {
            const code = item.fieldCode;
            if (!code) return;
            const label = code === 'dailyActivityMagnitude' ? '活跃度' : item.fieldName;
            if (!mergedMap.has(code)) {
              mergedMap.set(code, { label, value: code });
            }
          });
          setFieldOptions(Array.from(mergedMap.values()));
        }
      } catch (error) {
        console.error('获取属性列表失败', error);
      }
    };
    fetchFields();
  }, []);

  const handleEdit = async (record: TagRecord) => {
    try {
      const res = await getPetTagDetailApi({ id: record.id });
      if (res.code === 200 && res.data) {
        setModalMode('edit');
        setCurrentTag(res.data);
        const petTypeStr = res.data.petType === undefined || res.data.petType === null ? '' : String(res.data.petType);
        const petTypeValue = petTypeStr === '3' ? ['1', '2'] : petTypeStr ? [petTypeStr] : [];
        modalForm.setFieldsValue({
          tagName: res.data.tagName,
          petType: petTypeValue,
          attributes: res.data.fieldList?.map((item: any) => ({
            fieldCode: item.fieldCode,
            dictCode: String(item.dictCode),
          })),
        });
        const dictMapUpdates: Record<string, { label: string; value: string }[]> = {};
        if (res.data.fieldList?.length) {
          await Promise.all(
            res.data.fieldList.map(async (item: any) => {
              if (!item.fieldCode) return;
              const dictRes = await getPetFieldDictListApi(buildFieldDictParams(item.fieldCode));
              if (dictRes.code === 200) {
                dictMapUpdates[item.fieldCode] = formatDictOptions(item.fieldCode, dictRes.data || []);
              }
            }),
          );
        }
        if (Object.keys(dictMapUpdates).length) {
          setDictOptionsMap(prev => ({ ...prev, ...dictMapUpdates }));
        }
        setModalVisible(true);
      }
    } catch (error) {
      console.error('获取标签详情失败', error);
    }
  };

  const handleViewData = useCallback(async (record: TagRecord) => {
    setDataTitle(record.tagName);
    setDataDrawerVisible(true);
    setDataLoading(true);
    try {
      const petTypeStr = record.petType === undefined || record.petType === null ? '' : String(record.petType);
      setViewPetType(petTypeStr);
      const petType = petTypeStr === '3' || petTypeStr === '' ? '' : Number(petTypeStr);
      const res = await getPetTagDataApi({ id: record.id, petType });
      if ((res.code === 200 || res.code === 0) && res.data) {
        const payload = res.data?.data || res.data;
        setChartData(payload || { petTagList: [], dogTagList: [], catTagList: [] });
      }
    } catch (error) {
      console.error('获取标签数据失败', error);
    } finally {
      setDataLoading(false);
    }
  }, []);

  const handleToggleShow = useCallback(async (record: TagRecord, checked: boolean) => {
    setToggleId(record.id);
    try {
      const detailRes = await getPetTagDetailApi({ id: record.id });
      if (detailRes.code !== 200 || !detailRes.data) {
        return;
      }
      const detail = detailRes.data;
      const dictList =
        (detail.fieldList || []).map((item: any) => ({
          fieldCode: item.fieldCode,
          fieldName: item.fieldName,
          dictCode: item.dictCode,
          dictName: item.dictName,
        })) || [];
      const petTypeVal = detail.petType ?? record.petType;
      const petType =
        petTypeVal === undefined || petTypeVal === null || petTypeVal === '' ? undefined : Number(petTypeVal);
      const payload = {
        id: detail.id || record.id,
        tagName: detail.tagName || record.tagName,
        petType,
        isShow: checked ? 1 : 0,
        sortNum: detail.sortNum ?? record.sortNum ?? 0,
        dictList,
      };
      const res = await updatePetTagApi(payload);
      if (res.code === 200 || res.code === 0) {
        message.success(checked ? '已显示' : '已隐藏');
        setDataSource(prev => prev.map(item => (item.id === record.id ? { ...item, isShow: checked ? 1 : 0 } : item)));
      }
    } catch (error) {
      console.error('更新显示状态失败', error);
    } finally {
      setToggleId(null);
    }
  }, []);

  const columns: ColumnsType<TagRecord> = useMemo(
    () => [
      { title: '标签', dataIndex: 'tagName', width: 160 },
      {
        title: '分类',
        dataIndex: 'petType',
        width: 140,
        render: value => {
          const petTypeVal = String(value ?? '');
          if (petTypeVal === '1') return '狗';
          if (petTypeVal === '2') return '猫';
          if (petTypeVal === '3') return '猫、狗';
          return '-';
        },
      },
      {
        title: '属性-值',
        dataIndex: 'fieldNameList',
        width: 260,
        render: list =>
          list?.length ? list.map((name: string) => <AntTag key={name}>{name}</AntTag>) : <span>-</span>,
      },
      {
        title: '是否显示',
        dataIndex: 'isShow',
        width: 120,
        render: (value, record) => (
          <Switch
            checked={value === 1}
            loading={toggleId === record.id}
            disabled={!canToggleShow}
            onChange={checked => handleToggleShow(record, checked)}
          />
        ),
      },
      { title: '创建时间', dataIndex: 'createTime', width: 180 },
      {
        title: '操作',
        width: 180,
        fixed: 'right',
        render: (_, record) => (
          <Space size={15}>
            {canEdit && (
              <Button
                style={{ padding: 0 }}
                type="link"
                onClick={() => handleEdit(record)}>
                编辑
              </Button>
            )}
            {canDelete && (
              <Button
                style={{ padding: 0 }}
                type="link"
                danger
                onClick={() =>
                  Modal.confirm({
                    title: '确认删除该标签？',
                    onOk: async () => {
                      try {
                        const res = await deletePetTagApi({ id: record.id });
                        if (res.code === 200) {
                          message.success('删除成功');
                          fetchList({ pageNum: 1 });
                        }
                      } catch (error) {
                        console.error('删除标签失败', error);
                      }
                    },
                  })
                }>
                删除
              </Button>
            )}
            {canViewData && (
              <Button
                style={{ padding: 0 }}
                type="link"
                onClick={() => handleViewData(record)}>
                数据
              </Button>
            )}
            {canSortUp && (
              <Button
                style={{ padding: 0 }}
                type="link"
                disabled={record.sortNum === (dataSource[0]?.sortNum ?? record.sortNum)}
                onClick={async () => {
                  try {
                    const res = await updatePetTagSortApi({ id: record.id, sortNum: record.sortNum, sortType: 'UP' });
                    if (res.code === 200) {
                      message.success('上移成功');
                      fetchList({ pageNum: 1 });
                    }
                  } catch (error) {
                    console.error('上移动作失败', error);
                  }
                }}>
                上移
              </Button>
            )}
            {canSortDown && (
              <Button
                style={{ padding: 0 }}
                type="link"
                disabled={record.sortNum === (dataSource[dataSource.length - 1]?.sortNum ?? record.sortNum)}
                onClick={async () => {
                  try {
                    const res = await updatePetTagSortApi({ id: record.id, sortNum: record.sortNum, sortType: 'DOWN' });
                    if (res.code === 200) {
                      message.success('下移成功');
                      fetchList({ pageNum: 1 });
                    }
                  } catch (error) {
                    console.error('下移动作失败', error);
                  }
                }}>
                下移
              </Button>
            )}
          </Space>
        ),
      },
    ],
    [handleEdit, handleViewData, handleToggleShow, toggleId],
  );

  useEffect(() => {
    if (!dataDrawerVisible) {
      setChartData({ petTagList: [], dogTagList: [], catTagList: [] });
      setViewPetType('');
    }
  }, [dataDrawerVisible]);

  const pieData = useMemo(() => (chartData?.petTagList || []).filter((item: any) => item?.petCount), [chartData]);
  const petPieOption = useMemo(() => {
    return {
      tooltip: { trigger: 'item' },
      legend: { show: false },
      series: [
        {
          type: 'pie',
          radius: ['55%', '75%'],
          label: {
            formatter: '{b} {c}',
          },
          data: pieData.map((item: any) => ({ value: Number(item.petCount) || 0, name: item.name })),
        },
      ],
      color: ['#5CC1C7', '#4E7DFC', '#FFA940', '#73d13d'],
    };
  }, [pieData]);

  const viewPetTypeStr = useMemo(() => {
    if (viewPetType === undefined || viewPetType === null) return '';
    return String(viewPetType);
  }, [viewPetType]);
  const dogBreedList = useMemo(() => (chartData?.dogTagList || []).filter((item: any) => item?.petCount), [chartData]);
  const catBreedList = useMemo(() => (chartData?.catTagList || []).filter((item: any) => item?.petCount), [chartData]);
  const showDogCard = viewPetTypeStr === '' || viewPetTypeStr === '3' || viewPetTypeStr === '1';
  const showCatCard = viewPetTypeStr === '' || viewPetTypeStr === '3' || viewPetTypeStr === '2';
  const breedColSpan = showDogCard && showCatCard ? 12 : 24;

  const buildBarOption = useCallback((list: any[], color: string) => {
    const filtered = (list || []).filter(it => it && it.petCount);
    const names = filtered.map(it => it.name).reverse();
    const values = filtered.map(it => Number(it.petCount) || 0).reverse();
    return {
      grid: { left: 130, right: 20, top: 20, bottom: 20 },
      xAxis: { type: 'value', splitLine: { show: false } },
      yAxis: {
        type: 'category',
        data: names,
        axisTick: { show: false },
        axisLabel: {
          interval: 0,
          width: 120,
          margin: 6,
          overflow: 'breakAll',
          lineHeight: 16,
        },
      },
      series: [
        {
          type: 'bar',
          data: values,
          itemStyle: { color },
          barWidth: 16,
        },
      ],
    };
  }, []);

  return (
    <div style={{ padding: 24 }}>
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: 16, rowGap: 12 }}
        preserve={false}>
        <Form.Item
          label="宠物分类"
          name="petType">
          <Select
            allowClear
            placeholder="全部"
            style={{ width: 160 }}
            options={petTypeOptions}
          />
        </Form.Item>
        <Form.Item
          label="标签"
          name="tagName">
          <Input
            placeholder="请输入"
            style={{ width: 220 }}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="是否显示"
          name="isShow">
          <Select
            allowClear
            placeholder="全部"
            style={{ width: 140 }}
            options={displayOptions}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => fetchList({ pageNum: 1 })}>
              查询
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                form.resetFields();
                setPageNum(1);
                fetchList({ pageNum: 1 });
              }}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <div style={{ marginBottom: 12 }}>
        {canAdd && (
          <Button
            type="primary"
            onClick={() => {
              setModalMode('create');
              setCurrentTag(null);
              modalForm.resetFields();
              modalForm.setFieldsValue({ petType: [] });
              setModalVisible(true);
            }}>
            新建标签
          </Button>
        )}
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (page, size) => fetchList({ pageNum: page, pageSize: size }),
        }}
        scroll={{ x: 1000 }}
      />
      <Modal
        title={modalMode === 'create' ? '新建标签' : '编辑标签'}
        open={modalVisible}
        confirmLoading={modalSubmitting}
        onCancel={() => {
          setModalVisible(false);
          setCurrentTag(null);
          setModalMode('create');
          modalForm.resetFields();
        }}
        onOk={async () => {
          try {
            const values = await modalForm.validateFields();
            if (!values.petType || !values.petType.length) {
              message.error('请选择宠物分类');
              return;
            }
            const petTypeValue = values.petType.includes('1') && values.petType.includes('2') ? '3' : values.petType[0];
            const petType = Number(petTypeValue);
            const dictList =
              values.attributes?.map((item: any) => {
                const fallbackField = currentTag?.fieldList?.find((f: any) => f.fieldCode === item.fieldCode);
                const fieldName =
                  fieldOptions.find(opt => opt.value === item.fieldCode)?.label || fallbackField?.fieldName;
                const dictOption = dictOptionsMap[item.fieldCode]?.find(opt => opt.value === item.dictCode);
                const dictName = dictOption?.label || fallbackField?.dictName;
                return {
                  fieldCode: item.fieldCode,
                  fieldName,
                  dictCode: item.dictCode,
                  dictName,
                };
              }) || [];
            setModalSubmitting(true);
            let res: any;
            if (modalMode === 'create') {
              const payload = {
                tagName: values.tagName,
                petType,
                isShow: 1,
                dictList,
              };
              res = await addPetTagApi(payload);
            } else if (currentTag) {
              const payload = {
                id: currentTag.id,
                tagName: values.tagName,
                petType,
                isShow: currentTag.isShow ?? 1,
                sortNum: currentTag.sortNum ?? 0,
                dictList,
              };
              res = await updatePetTagApi(payload);
            }
            if (res?.code === 200) {
              message.success(modalMode === 'create' ? '添加成功' : '更新成功');
              setModalVisible(false);
              fetchList({ pageNum: 1 });
            }
          } catch (error: any) {
            if (error?.errorFields) return;
            console.error('添加标签失败', error);
          } finally {
            setModalSubmitting(false);
          }
        }}>
        <Form
          form={modalForm}
          layout="horizontal"
          labelCol={{ flex: '90px' }}
          labelAlign="left">
          <Form.Item
            label="标签名称"
            name="tagName"
            rules={[
              { required: true, message: '请输入标签名称' },
              { pattern: /^\S{1,6}$/, message: '最多6个字符，且不可包含空格' },
            ]}>
            <Input
              allowClear
              showCount
              placeholder="请输入标签名称"
              maxLength={6}
            />
          </Form.Item>
          <Form.Item
            label="宠物分类"
            name="petType"
            rules={[{ required: true, message: '请选择宠物分类' }]}>
            <Checkbox.Group>
              <Checkbox value="1">狗</Checkbox>
              <Checkbox value="2">猫</Checkbox>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item
            label="关联属性值"
            required>
            <Form.List
              name="attributes"
              rules={[
                {
                  validator: async (_, value) => {
                    if (value && value.length > 0) return;
                    return Promise.reject(new Error('请选择关联属性值'));
                  },
                },
              ]}>
              {(fields, { add, remove }, { errors }) => (
                <div>
                  {fields.map(field => {
                    const fieldCode = modalForm.getFieldValue(['attributes', field.name, 'fieldCode']);
                    const attributes: any[] = modalForm.getFieldValue('attributes') || [];
                    const usedDictCodes =
                      attributes
                        ?.filter(
                          (_: any, idx: number) => idx !== field.name && attributes[idx]?.fieldCode === fieldCode,
                        )
                        .map((item: any) => item?.dictCode) || [];
                    const dictOptions = (dictOptionsMap[fieldCode] || []).map((opt: any) => ({
                      ...opt,
                      disabled: usedDictCodes.includes(opt.value),
                    }));
                    return (
                      <Space
                        key={field.key}
                        style={{ display: 'flex', marginBottom: 8 }}
                        align="baseline">
                        <Form.Item
                          {...field}
                          name={[field.name, 'fieldCode']}
                          rules={[{ required: true, message: '请选择属性' }]}>
                          <Select
                            placeholder="请选择属性"
                            style={{ width: 160 }}
                            options={fieldOptions}
                            onChange={async value => {
                              try {
                                if (!value) return;
                                const res = await getPetFieldDictListApi(buildFieldDictParams(value));
                                if (res.code === 200) {
                                  setDictOptionsMap(prev => ({
                                    ...prev,
                                    [value]: formatDictOptions(value, res.data || []),
                                  }));
                                }
                              } catch (error) {
                                console.error('获取属性值失败', error);
                              }
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, 'dictCode']}
                          rules={[{ required: true, message: '请选择属性值' }]}>
                          <Select
                            placeholder="请选择属性值"
                            style={{ width: 160 }}
                            options={dictOptions}
                          />
                        </Form.Item>
                        <Button
                          type="link"
                          danger
                          onClick={() => remove(field.name)}>
                          删除
                        </Button>
                      </Space>
                    );
                  })}
                  {fields.length < 3 && (
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      style={{ width: '100%' }}>
                      + 添加属性
                    </Button>
                  )}
                  <Form.ErrorList errors={errors} />
                </div>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={`标签数据 - ${dataTitle || ''}`}
        width={850}
        open={dataDrawerVisible}
        onClose={() => setDataDrawerVisible(false)}
        destroyOnHidden>
        <Spin spinning={dataLoading}>
          {dataDrawerVisible ? (
            <>
              <Typography.Title
                level={5}
                style={{ marginBottom: 12 }}>
                宠物类型数量分布
              </Typography.Title>
              <Card
                size="small"
                styles={{
                  body: {
                    padding: 12,
                    minHeight: 260,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                }}>
                {pieData.length ? (
                  <ReactECharts
                    option={petPieOption}
                    notMerge
                    lazyUpdate
                    key={`pie-${dataTitle}-${pieData.length}`}
                    style={{ height: 240, width: '100%' }}
                  />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="暂无数据"
                  />
                )}
              </Card>
              <Divider />
              <Typography.Title
                level={5}
                style={{ marginBottom: 12 }}>
                宠物品种数量分布（Top5）
              </Typography.Title>
              {showDogCard || showCatCard ? (
                <Row gutter={12}>
                  {showDogCard && (
                    <Col span={breedColSpan}>
                      <Card
                        size="small"
                        title="狗"
                        styles={{
                          body: {
                            padding: 12,
                            minHeight: 260,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          },
                        }}>
                        {dogBreedList.length ? (
                          <ReactECharts
                            option={buildBarOption(dogBreedList, '#5CC1C7')}
                            notMerge
                            lazyUpdate
                            key={`dog-${dogBreedList.length}`}
                            style={{ height: 240, width: '100%' }}
                          />
                        ) : (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="暂无数据"
                          />
                        )}
                      </Card>
                    </Col>
                  )}
                  {showCatCard && (
                    <Col span={breedColSpan}>
                      <Card
                        size="small"
                        title="猫"
                        styles={{
                          body: {
                            padding: 12,
                            minHeight: 260,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          },
                        }}>
                        {catBreedList.length ? (
                          <ReactECharts
                            option={buildBarOption(catBreedList, '#4E7DFC')}
                            notMerge
                            lazyUpdate
                            key={`cat-${catBreedList.length}`}
                            style={{ height: 240, width: '100%' }}
                          />
                        ) : (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="暂无数据"
                          />
                        )}
                      </Card>
                    </Col>
                  )}
                </Row>
              ) : (
                <Card
                  size="small"
                  styles={{
                    body: {
                      padding: 12,
                      minHeight: 260,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                  }}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="暂无数据"
                  />
                </Card>
              )}
            </>
          ) : null}
        </Spin>
      </Drawer>
    </div>
  );
};

export default TagManage;
